import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Lock, Mail, User, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login, register, isAuthenticated, loading: authLoading } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="spinner" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isRegister) {
        await register(formData.email, formData.password, formData.name);
      } else {
        await login(formData.email, formData.password);
      }
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen pt-32 pb-24 flex items-center justify-center bg-neutral-50">
      <div className="container-custom max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-10 border border-neutral-200"
        >
          <div className="text-center mb-10">
            <span className="text-neutral-400 uppercase tracking-[0.2em] text-sm">
              Admin Portal
            </span>
            <h1 className="text-3xl font-bold tracking-tight mt-2">
              {isRegister ? 'Create Account' : 'Welcome Back'}
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {isRegister && (
              <div className="relative">
                <User size={18} className="absolute left-0 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Full name"
                  required={isRegister}
                  data-testid="admin-name-input"
                  className="input-underline pl-8"
                />
              </div>
            )}

            <div className="relative">
              <Mail size={18} className="absolute left-0 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email address"
                required
                data-testid="admin-email-input"
                className="input-underline pl-8"
              />
            </div>

            <div className="relative">
              <Lock size={18} className="absolute left-0 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Password"
                required
                data-testid="admin-password-input"
                className="input-underline pl-8"
              />
            </div>

            {error && (
              <p className="text-red-500 text-sm">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-submit-button"
              className="w-full btn-primary flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="spinner w-5 h-5" />
              ) : (
                <>
                  {isRegister ? 'Create Account' : 'Sign In'}
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          <div className="mt-8 text-center">
            <button
              onClick={() => {
                setIsRegister(!isRegister);
                setError('');
              }}
              data-testid="toggle-auth-mode"
              className="text-neutral-500 hover:text-black text-sm transition-colors"
            >
              {isRegister
                ? 'Already have an account? Sign in'
                : "Don't have an account? Create one"}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminLogin;
